import { Editor } from '@tiptap/react';

export interface FootnoteGroupInfo {
  isFirstInGroup: boolean;
  groupSize: number;
  groupCiteIds: string[];
}

export const getFootnoteGroupInfo = (
  editor: Editor,
  currentPos: number | (() => number | undefined),
): FootnoteGroupInfo => {
  const pos = typeof currentPos === 'function' ? currentPos() : currentPos;

  const defaultResult: FootnoteGroupInfo = {
    isFirstInGroup: false,
    groupSize: 0,
    groupCiteIds: [],
  };

  if (pos === null || pos === undefined || typeof pos !== 'number') {
    return defaultResult;
  }

  const { doc } = editor.state;
  const consecutiveFootnotes: Array<{ pos: number; citeId: string }> = [];

  // First, go backwards to find the start of any consecutive group
  let beforePos = pos;
  while (beforePos >= 0) {
    const resolvedPos = doc.resolve(beforePos);
    const nodeBefore = resolvedPos.nodeBefore;

    if (nodeBefore?.type.name === 'footnoteNode') {
      consecutiveFootnotes.unshift({
        pos: beforePos - nodeBefore.nodeSize,
        citeId: nodeBefore.attrs.citeId,
      });
      beforePos -= nodeBefore.nodeSize;
    } else if (nodeBefore?.type.name === 'text') {
      // Check if it's just whitespace
      const text = nodeBefore.text ?? '';
      if (text.trim().length === 0) {
        beforePos -= nodeBefore.nodeSize;
        continue;
      }
      break;
    } else {
      break;
    }
  }

  // Add current footnote
  const currentNode = doc.nodeAt(pos);
  if (currentNode?.type.name === 'footnoteNode') {
    consecutiveFootnotes.push({
      pos,
      citeId: currentNode.attrs.citeId,
    });
  }

  // Now go forward to find remaining consecutive footnotes
  let afterPos = pos + (currentNode?.nodeSize ?? 1);
  while (afterPos < doc.content.size) {
    const resolvedPos = doc.resolve(afterPos);
    const nodeAfter = resolvedPos.nodeAfter;

    if (nodeAfter?.type.name === 'footnoteNode') {
      consecutiveFootnotes.push({
        pos: afterPos,
        citeId: nodeAfter.attrs.citeId,
      });
      afterPos += nodeAfter.nodeSize;
    } else if (nodeAfter?.type.name === 'text') {
      // Check if it's just whitespace
      const text = nodeAfter.text ?? '';
      if (text.trim().length === 0) {
        afterPos += nodeAfter.nodeSize;
        continue;
      }
      break;
    } else {
      break;
    }
  }

  // Determine if this forms a group
  // Deduplicate by citeId to match what the popover will show
  const uniqueCiteIds = [...new Set(consecutiveFootnotes.map((f) => f.citeId))];
  const groupSize = uniqueCiteIds.length;
  const isFirstInGroup = consecutiveFootnotes[0]?.pos === pos;

  return {
    isFirstInGroup,
    groupSize,
    groupCiteIds: uniqueCiteIds,
  };
};
